<script setup lang="ts">
import { computed, useTemplateRef } from "vue";
import { UiButton, UiForm, UiFormField, UiInfoBox, UiInputField, UiSelect, UiTextarea } from "@shopexperts/shared";
import { bannedEmailFormSchema, type BannedEmailFormValues } from "@/app/composables/ban-list/banListForms";

const props = defineProps<{
    isSubmitting?: boolean;
    ruleOptions: readonly { label: string; value: string }[];
    serverErrors?: Record<string, string | undefined>;
}>();

const emit = defineEmits<{
    (e: "submit", values: BannedEmailFormValues): void;
    (e: "cancel"): void;
    (e: "change"): void;
}>();

const model = defineModel<BannedEmailFormValues>({ required: true });

const formRef = useTemplateRef<{ resetForm: () => void; forceClear: () => void }>("formRef");

const ruleSelectOptions = computed(() => [
    { label: "No rule (manual ban)", value: "" },
    ...props.ruleOptions,
]);

defineExpose({
    resetForm: () => {
        formRef.value?.resetForm();
        formRef.value?.forceClear();
    },
});
</script>

<template>
    <UiForm
        ref="formRef"
        :schema="bannedEmailFormSchema"
        :initial-values="model"
        v-model="model"
        :server-errors="props.serverErrors"
        scroll-to-first-error
        @update:model-value="emit('change')"
        @submit="(values: BannedEmailFormValues) => emit('submit', values)"
    >
        <div class="flex flex-col gap-5">
            <UiInfoBox variant="warning">
                A manual ban always locks the account behind the address, if there is one. Unbanning restores it.
            </UiInfoBox>

            <UiFormField name="email" label="Email address">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiInputField
                        :id="id"
                        name="email"
                        type="email"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="props.isSubmitting"
                        placeholder="spammer@example.com"
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="ban_rule_id" label="Attribute to a rule">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiSelect
                        :id="id"
                        name="ban_rule_id"
                        :options="(ruleSelectOptions as { label: string; value: string }[])"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="props.isSubmitting"
                        placeholder="No rule (manual ban)"
                        hint="Optional. Links the ban to a rule for reporting — the ban is still recorded as manual."
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <UiFormField name="notes" label="Notes">
                <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
                    <UiTextarea
                        :id="id"
                        name="notes"
                        :model-value="modelValue as string"
                        :invalid="invalid"
                        :error="error"
                        :disabled="props.isSubmitting"
                        :rows="3"
                        placeholder="Reported by the matching team."
                        hint="Optional. Why this address was banned by hand."
                        @update:model-value="onUpdateModelValue"
                        @validate="onValidate"
                    />
                </template>
            </UiFormField>

            <div class="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                <UiButton variant="outline" label="Cancel" :disabled="props.isSubmitting" @click="emit('cancel')" />
                <UiButton type="submit" variant="primary" label="Ban Email" :disabled="props.isSubmitting" />
            </div>
        </div>
    </UiForm>
</template>
